import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";
import { Brand } from "@/components/Brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <Brand variant="auth" />

        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-4" data-testid="login-form">
          <div>
            <h2 className="font-display text-lg font-bold text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500">Enter your staff credentials to continue.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input data-testid="login-username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. admin" autoComplete="username" autoCapitalize="none" required />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </div>

          <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</> : "Sign in"}
          </Button>

          <div className="text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-slate-500 hover:text-slate-900">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
